import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import { salesActions } from "@src/store";
import { Button, ButtonVariant } from "../button/Button";
import { Dropdown, IItems } from "../dropdown/Dropdown";
import { Switch } from "../switch/Switch";
import helperStyles from "@src/components/helpers.module.scss";
import { clx } from "../utils/stringUtils";
import { OptionType } from "@src/components/utils/reactSelectUtils";
import InvoiceForm from "@src/components/invoiceForm/InvoiceForm";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import BulkInvoiceItem from "./BulkInvoiceItem";
import styles from "./BulkInvoice.module.scss";

type TBulkInvoice = {
	handleClose?: () => void;
};

const BULK_SALE_PRICE = 0;
const DEFAULT_EXCHANGE_NAME = "External Invoice";

const BulkInvoice = ({ handleClose }: TBulkInvoice) => {
	const dispatch = useAppDispatch();

	const items = useAppSelector(selectSelectedInventoryItems);
	const exchanges = useAppSelector((state) => state.sales.exchanges);
	const submitting = useAppSelector((state) => state.sales.createExternalInvoiceBulk.loading);
	const eventInventoryData = useAppSelector((state) => state.pricing.eventInventoryAll.data);
	const selectionEvent = useAppSelector((state) => state.pricing.selection.event);

	const [exchangeName, setExchangeName] = useState("");
	const [zeroCost, setZeroCost] = useState(false);
	const [removedIds, setRemovedIds] = useState<Array<number>>([]);

	const workingItems = useMemo(() => items.filter((x) => !removedIds.includes(x.Listing_ID)), [items, removedIds]);

	const marketplaceOptions = useMemo(() => exchanges.data.filter((x) => x.Show_On_Invoice), [exchanges.version]);

	const selectedExchange = useMemo(
		() => marketplaceOptions.find((x) => x.Exchange_Name === exchangeName),
		[marketplaceOptions, exchangeName],
	);

	const marketplaceItems: Array<IItems> = useMemo(
		() => marketplaceOptions.map((x) => ({ id: x.Customer_ID ?? 0, value: x.Exchange_Name, label: x.Exchange_Name })),
		[marketplaceOptions],
	);

	useEffect(() => {
		if (!exchanges.init) dispatch(salesActions.listExchanges({}));
	}, []);

	useEffect(() => {
		if (exchangeName || !marketplaceOptions.length) return;
		const def = marketplaceOptions.find((x) => x.Exchange_Name === DEFAULT_EXCHANGE_NAME) ?? marketplaceOptions[0];
		setExchangeName(def.Exchange_Name);
	}, [marketplaceOptions]);

	const handleRemoveItem = (listingId: number) => {
		setRemovedIds((prev) => (prev.includes(listingId) ? prev : [...prev, listingId]));
	};

	const listingsCount = workingItems.length;
	const ticketsCount = workingItems.reduce((acc, x) => acc + (x.Quantity || 0), 0);
	const eventsCount = new Set(workingItems.map((x) => x.Event_ID)).size;

	const canBulkInvoice = listingsCount >= 2;
	const singleItem = listingsCount === 1 ? workingItems[0] : null;

	const singleSeats = useMemo<Array<OptionType<number>>>(() => {
		if (!singleItem) return [];
		return eventInventoryData
			.filter((x) => x.Listing_ID === singleItem.Listing_ID && !x.Invoice_ID)
			.map((x) => ({ value: x.Seat_Number, label: String(x.Seat_Number) }));
	}, [eventInventoryData, singleItem]);

	const handleSubmit = async () => {
		if (!canBulkInvoice || !selectedExchange) return;
		try {
			await dispatch(
				salesActions.createExternalInvoiceBulk({
					ListingIds: workingItems.map((x) => x.Listing_ID),
					ExchangeName: selectedExchange.Exchange_Name,
					ExchangeCustomerID: selectedExchange.Customer_ID == null ? "null" : String(selectedExchange.Customer_ID),
					ZeroCost: zeroCost,
				}),
			).unwrap();
			toast.success("Bulk invoice created.");
			handleClose?.();
		} catch {
			toast.error("Failed to create bulk invoice.");
		}
	};

	if (singleItem) {
		return <InvoiceForm item={singleItem} event={selectionEvent} availableSeats={singleSeats} onClose={handleClose} />;
	}

	const mappedItems = workingItems.map((x) => (
		<BulkInvoiceItem key={x.Item_ID} data={x} salePrice={BULK_SALE_PRICE} onRemove={handleRemoveItem} />
	));

	return (
		<div className={styles.root}>
			<EventContextHeader
				padding
				listings={workingItems.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))}
			/>
			<div className={styles.top}>
				<div className={styles.left}>
					<div className={styles.fieldLabel}>Sale Price</div>
					<div className={styles.inputRow}>
						<div className={styles.currencyPrefix}>$</div>
						<input className={styles.costInput} type="number" value={BULK_SALE_PRICE} disabled readOnly />
					</div>
					<div className={styles.lockRow}>
						<Switch
							checked={zeroCost}
							onChange={() => setZeroCost((prev) => !prev)}
							label="Zero out cost"
							isDisabled={!canBulkInvoice}
						/>
					</div>
					<Dropdown
						className={styles.marketplaceDropdown}
						label="Marketplace"
						theme="secondary"
						containerWidth="100%"
						isDisabled={!canBulkInvoice}
						buttonText={exchangeName || DEFAULT_EXCHANGE_NAME}
						items={marketplaceItems}
						onItemSelect={(item) => setExchangeName(String(item.value))}
					/>
					{canBulkInvoice ? (
						<div className={styles.warning}>
							<div className={styles.warningHeader}>Multiple listings selected!</div>
							<div className={styles.warningText}>Bulk invoicing will ZERO out the price!</div>
						</div>
					) : (
						<div className={styles.warning}>
							<div className={styles.warningHeader}>At least 2 listings required.</div>
							<div className={styles.warningText}>Bulk Invoice applies to multiple listings only.</div>
						</div>
					)}
				</div>
				<div className={styles.right}>
					<div className={styles.rightTopHeaders}>
						<div className={styles.subHeader}>Changes Summary</div>
						<div className={styles.subHeaderSecondary}>
							{listingsCount} listings · {ticketsCount} tickets · {eventsCount} events selected
						</div>
					</div>
					<div data-no-border="true" className={styles.singleListing}>
						<div className={clx(styles.secRowSeat, styles.headerSecRowSeat)}>
							<div className={clx(styles.itemTextPrimary, helperStyles.ellipsisDiv)}>Sec, Row, Seat</div>
						</div>
						<div className={styles.salePrice}>Sale Price</div>
						<div className={styles.xContainer}></div>
					</div>
					<div className={styles.listings}>{mappedItems}</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button onClick={handleClose} variant={ButtonVariant.Secondary} disabled={submitting}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					isLoading={submitting}
					disabled={!canBulkInvoice || !selectedExchange || submitting}>
					Submit
				</Button>
			</div>
		</div>
	);
};

export default BulkInvoice;
